import { useCallback } from "react";
import { Eyebrow } from "~/components/ui/Typography";
import { cn } from "~/lib/utils";

type PhoneFieldProps = {
  id: string;
  label: string;
  value: string;
  error?: string;
  onChange: (e164: string) => void;
};

/** Format raw digits (up to 10) as (555) 123-4567 */
function formatDisplay(digits: string): string {
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/** Strip everything except digits from the input */
function stripNonDigits(value: string): string {
  return value.replace(/\D/g, "");
}

export function PhoneField({
  id,
  label,
  value,
  error,
  onChange,
}: PhoneFieldProps) {
  // value is E.164 like "+15551234567", extract the national digits
  const nationalDigits = value.startsWith("+1")
    ? value.slice(2)
    : stripNonDigits(value);
  const displayValue = formatDisplay(nationalDigits);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = stripNonDigits(e.target.value).slice(0, 10);
      onChange(raw.length > 0 ? `+1${raw}` : "");
    },
    [onChange],
  );

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id}>
        <Eyebrow className="text-secondary">{label}</Eyebrow>
      </label>

      <div
        className={cn(
          "flex w-full items-center rounded-xl border bg-surface-2",
          "transition-colors focus-within:border-accent focus-within:bg-surface",
          error ? "border-danger focus-within:border-danger" : "border-border",
        )}
      >
        <span className="shrink-0 pl-3.5 text-[14px] font-medium text-muted select-none">
          +1
        </span>
        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder="(415) 867-5309"
          value={displayValue}
          onChange={handleChange}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={error ? `${id}-error` : undefined}
          className={cn(
            "w-full bg-transparent px-2 py-[11px] text-[14px] font-medium text-body",
            "placeholder:font-normal placeholder:text-muted",
            "outline-none",
          )}
        />
      </div>

      <span
        id={`${id}-error`}
        className="min-h-4 text-xs font-medium text-danger"
        role="alert"
      >
        {error ?? ""}
      </span>
    </div>
  );
}
